<!--
  ═══════════════════════════════════════════════════════════════════════════
  WRAPPER - ATRIBUTOS DINÁMICOS PARA PROCESO
  ═══════════════════════════════════════════════════════════════════════════
  
  Wrapper específico para renderizar atributos dinámicos de Proceso.
  Maneja criterios de visibilidad por Módulo y ElementoTipo.
  
  Uso:
  ```vue
  <ProcesoAtributosForm
    v-model="proceso.AtributosJSON"
    :codigo-elemento-tipo="cotizacion.CodigoElementoTipo"
    :modulo="2"
    @change="handleAtributosChange"
  />
  ```
  
  @author Sistema SISPRO ERP
  @version 1.0.0
-->

<template>
  <AtributosDinamicosForm
    v-if="shouldShowComponent"
    ref="formRef"
    :definiciones="definiciones"
    :model-value="modelValue"
    :readonly="readonly"
    :layout="layout"
    :columns="columns"
    :show-validation-errors="showValidationErrors"
    @update:modelValue="emit('update:modelValue', $event)"
    @validation-error="emit('validation-error', $event)"
    @change="emit('change', $event)"
  />
</template>

<script setup>
import {
  ref,
  computed,
  onMounted,
  watch,
} from 'vue';
import { ElMessage } from 'element-plus';
import AtributosDinamicosForm from './AtributosDinamicosForm.vue';
import atributosDinamicosService from '@/services/atributos_dinamicos_service';

// ══════════════════════════════════════════════════════════════════════════
// PROPS & EMITS
// ══════════════════════════════════════════════════════════════════════════

const props = defineProps({
  modelValue: {
    type: [Object, String],
    default: () => ({}),
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  layout: {
    type: String,
    default: 'grid',
  },
  columns: {
    type: Number,
    default: 2,
  },
  showValidationErrors: {
    type: Boolean,
    default: false,
  },
  // Criterios de visibilidad específicos de Proceso
  codigoElementoTipo: {
    type: Number,
    default: null,
  },
  codigoProceso: {
    type: Number,
    default: null,
  },
  modulo: {
    type: Number,
    default: null,
  },
});

const emit = defineEmits(['update:modelValue', 'validation-error', 'change']);

// ══════════════════════════════════════════════════════════════════════════
// ESTADO
// ══════════════════════════════════════════════════════════════════════════

const formRef = ref(null);
const definiciones = ref([]);
const loading = ref(false);

// ══════════════════════════════════════════════════════════════════════════
// COMPUTED
// ══════════════════════════════════════════════════════════════════════════

/**
 * Determina si el componente debe mostrarse
 * Se oculta cuando:
 * - El contexto NO es 'abm' (hay módulo específico)
 * - Y no hay definiciones disponibles
 */
const shouldShowComponent = computed(() => {
  const isAbmContext = !props.modulo;
  const hasDefiniciones = definiciones.value.length > 0;

  // En contexto ABM siempre mostrar (aunque esté vacío, para dar feedback)
  if (isAbmContext) {
    return true;
  }

  // En contexto de módulo específico, solo mostrar si hay definiciones
  return hasDefiniciones;
});

// ══════════════════════════════════════════════════════════════════════════
// MÉTODOS
// ══════════════════════════════════════════════════════════════════════════

/**
 * Carga definiciones de atributos para Proceso
 */
async function loadDefiniciones() {
  loading.value = true;
  try {
    // Usar el nuevo endpoint que filtra según criterios
    const opciones = {
      contexto: props.modulo ? 'modulo' : 'abm',
    };

    // Agregar módulo si está especificado
    if (props.modulo) {
      opciones.modulo = props.modulo;
    }

    // Agregar ElementoTipo si está especificado
    if (props.codigoElementoTipo) {
      opciones.codigoElementoTipo = props.codigoElementoTipo;
    }

    if (props.codigoProceso) {
      opciones.codigoProceso = props.codigoProceso;
    }

    const resultado = await atributosDinamicosService.getDefinicionesVisiblesConEditable(
      'Proceso',
      opciones,
    );

    definiciones.value = resultado;
  } catch (error) {
    console.error('Error cargando atributos de Proceso:', error);
    ElMessage.error('Error cargando configuración de atributos');
  } finally {
    loading.value = false;
  }
}

/**
 * Valida el formulario
 */
async function validate() {
  return await formRef.value?.validate();
}

/**
 * Resetea el formulario
 */
function reset() {
  formRef.value?.reset();
}

// ══════════════════════════════════════════════════════════════════════════
// LIFECYCLE
// ══════════════════════════════════════════════════════════════════════════

onMounted(() => {
  loadDefiniciones();
});

// Recargar cuando cambien criterios de visibilidad
watch(
  () => [props.codigoElementoTipo, props.modulo, props.codigoProceso],
  () => {
    loadDefiniciones();
  },
);

// ══════════════════════════════════════════════════════════════════════════
// EXPOSE
// ══════════════════════════════════════════════════════════════════════════

defineExpose({
  validate,
  reset,
  loadDefiniciones,
  // Propiedades computadas delegadas directamente desde el componente hijo
  get isValid() {
    return formRef.value?.isValid ?? true;
  },
  get atributos() {
    return formRef.value?.atributos ?? {};
  },
});
</script>
